import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowLeft, Layers } from "lucide-react";

export default function CourseCreate() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState(0);
  const [cover, setCover] = useState("");
  const [kind, setKind] = useState("cohort");
  const [startDate, setStartDate] = useState("");
  const [drip, setDrip] = useState(7);
  const [webinars, setWebinars] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/webinars").then(({ data }) => setWebinars(data)).catch(() => {});
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title, description: desc, price: Number(price) || 0,
        cover_image: cover || null, schedule_kind: kind,
        start_date: startDate ? new Date(startDate).toISOString() : null,
        drip_gap_days: Number(drip) || 3,
        webinar_ids: selected,
      };
      const { data } = await api.post("/courses", payload);
      toast.success("Course created");
      nav(`/app/courses/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 vp-fade-up max-w-3xl">
      <div>
        <Link to="/app/courses" className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to courses
        </Link>
        <div className="vp-label mt-3">New course</div>
        <h1 className="font-display text-3xl font-bold mt-1">Bundle webinars into a cohort</h1>
      </div>

      <form onSubmit={submit} className="vp-glass p-6 space-y-5">
        <div>
          <div className="vp-label mb-2">Course title</div>
          <input required className="vp-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid={TIDS.courseTitle} placeholder="Creator Growth Bootcamp" />
        </div>
        <div>
          <div className="vp-label mb-2">Description</div>
          <textarea required rows={3} className="vp-input" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid={TIDS.courseDesc} placeholder="A 3-week cohort covering audience, product, and launch." />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="vp-label mb-2">Price (₹, 0 = free)</div>
            <input type="number" min="0" className="vp-input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={TIDS.coursePrice} />
          </div>
          <div>
            <div className="vp-label mb-2">Cover image URL (optional)</div>
            <input className="vp-input" value={cover} onChange={(e) => setCover(e.target.value)} placeholder="https://…" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <div className="vp-label mb-2">Schedule</div>
            <select className="vp-input" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="cohort">Cohort (all attend live)</option>
              <option value="drip">Drip (unlock one at a time)</option>
            </select>
          </div>
          <div>
            <div className="vp-label mb-2">Start date</div>
            <input type="datetime-local" className="vp-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          {kind === "drip" && (
            <div>
              <div className="vp-label mb-2">Days between modules</div>
              <input type="number" min="1" max="60" className="vp-input" value={drip} onChange={(e) => setDrip(e.target.value)} />
            </div>
          )}
        </div>

        <div>
          <div className="vp-label mb-2 flex items-center gap-2"><Layers className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.5} /> Modules · pick webinars ({selected.length} selected)</div>
          <div className="space-y-2 max-h-64 overflow-auto vp-card p-3">
            {webinars.length === 0 ? (
              <div className="text-[#747680] text-sm">No webinars yet. <Link to="/app/webinars/new" className="text-[#0B5CFF] hover:underline">Create one first</Link></div>
            ) : webinars.map((w) => (
              <label key={w.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F6F7FA] cursor-pointer">
                <input type="checkbox" checked={selected.includes(w.id)} onChange={() => toggle(w.id)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{w.title}</div>
                  <div className="text-xs text-[#747680]">{new Date(w.scheduled_at).toLocaleString("en-IN")}</div>
                </div>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="text-xs text-[#747680] mt-2">
              Order matches selection sequence. In {kind === "drip" ? `drip mode, modules unlock every ${drip} days.` : "cohort mode, all attendees attend live sessions."}
            </div>
          )}
        </div>

        <button type="submit" disabled={saving || !title.trim()} className="vp-btn-primary" data-testid={TIDS.courseSubmit}>
          {saving ? "Creating…" : "Create course"}
        </button>
      </form>
    </div>
  );
}
